import { useHeaderConnection } from "@client/HeaderConnectionContext";
import { useNetworkStatus } from "@client/hooks/useNetworkStatus";
import { isIPadCapacitor } from "@client/lib/isIPad";
import { useOfflineAvailability } from "@client/offline/offlineAvailability";
import { formatRelativeTimeAgo } from "@client/utils/format";
import SPAStorageAdapterWrapper from "@engine/storageAdapters/SPAStorageAdapterWrapper";
import { CloudOffIcon } from "lucide-react";
import { useEffect, useState } from "react";

// iPad-only: the offline round cache is gated to the Capacitor iPad shell, so
// desktop/web and iPhone stay byte-identical and never see this banner.
const IPAD_OFFLINE = isIPadCapacitor();

/**
 * Round-scoped banner shown when the iPad is offline: tells the funder they're
 * looking at the last synced snapshot (and from when), plus how many of their
 * edits are queued to sync on reconnect. Driven by `useNetworkStatus` +
 * the cached-aggregate metadata + the unconfirmed-action queue.
 */
export function OfflineBanner({ roundId }: { roundId: string }) {
  const { isOnline } = useNetworkStatus();
  const availability = useOfflineAvailability(roundId);
  const connectionData = useHeaderConnection();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!connectionData) {
      setPendingCount(0);
      return;
    }
    let active = true;
    void connectionData.unconfirmedActions.then((actions) => {
      if (active) setPendingCount(actions.length);
    });
    return () => {
      active = false;
    };
  }, [connectionData, isOnline]);

  // Only claim "showing the last synced version" once the round has actually
  // loaded. If the offline load FAILED (no valid cached aggregate for this
  // session), the round body renders the "not available offline" error and no
  // connection data is published — showing the banner then would falsely tell
  // the funder they're viewing a cached snapshot. Gating on connectionData also
  // means the displayed `cachedAt` is the row this session genuinely loaded
  // from (not an unvalidated stray row).
  if (!IPAD_OFFLINE || isOnline || !connectionData) return null;

  const cachedAgo = availability.cachedAt
    ? formatRelativeTimeAgo(availability.cachedAt)
    : null;
  const cachedSuffix = cachedAgo ? ` (cached ${cachedAgo})` : "";

  // Edits that were queued before this offline launch but couldn't be replayed
  // onto the cached snapshot — they're still queued and WILL sync on reconnect,
  // they just aren't reflected in this session's view. Surface them so the
  // funder doesn't assume the edit was lost and redo it.
  const storage = connectionData.storage;
  const unshownCount =
    storage instanceof SPAStorageAdapterWrapper
      ? storage.getUnshownOfflineEditCount()
      : 0;

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 border-b border-slate-500/30 bg-slate-500/15 px-4 py-1.5 text-sm"
    >
      <CloudOffIcon className="size-4 shrink-0 text-slate-600 dark:text-slate-300" />
      <span>
        Offline — showing the last synced version{cachedSuffix}.
        {pendingCount > 0 && (
          <strong className="ml-1 font-semibold">
            {pendingCount} change{pendingCount === 1 ? "" : "s"} pending sync.
          </strong>
        )}
        {unshownCount > 0 && (
          <strong className="ml-1 font-semibold text-amber-700 dark:text-amber-400">
            {`⚠️ ${unshownCount} couldn’t be shown here — ${
              unshownCount === 1 ? "it’ll" : "they’ll"
            } sync when you reconnect.`}
          </strong>
        )}
      </span>
    </div>
  );
}
